import { useQuery, useQueryClient } from '@tanstack/react-query';
import { stockReturnsAPI } from '../../services/api';
import { StockReturnNew, StockReturnNewListResponse } from '../../types/stockReturnNew';

/**
 * Query hooks for Stock Returns (server-paginated list + deleted list).
 *
 * Key root MUST be 'stock-returns' — the slug the realtime-invalidation
 * middleware broadcasts for /api/stock-returns.
 */
export interface StockReturnsListParams {
  skip: number;
  limit: number;
  search?: string;
  extra?: Record<string, any>;
}

export const stockReturnsKeys = {
  root: ['stock-returns'] as const,
  list: (params: StockReturnsListParams) => ['stock-returns', 'list', params] as const,
  deleted: ['stock-returns', 'deleted'] as const,
};

export function useStockReturnsList(params: StockReturnsListParams, enabled: boolean) {
  return useQuery<StockReturnNewListResponse>({
    queryKey: stockReturnsKeys.list(params),
    queryFn: () =>
      stockReturnsAPI.getPaginated(
        params.skip,
        params.limit,
        params.search,
        undefined,
        undefined,
        undefined,
        params.extra,
      ),
    enabled,
    keepPreviousData: true,
  });
}

export function useDeletedStockReturns(enabled: boolean) {
  return useQuery<StockReturnNew[]>({
    queryKey: stockReturnsKeys.deleted,
    queryFn: () => stockReturnsAPI.getDeleted(),
    enabled,
  });
}

export function useInvalidateStockReturns() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: stockReturnsKeys.root });
}
